import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import {
  AbstractComponent,
  ChatCommandTypeEnum,
  ChatSearchPerson,
  ChatService,
  ChatSocketService,
  ChatUserDisplayMap,
} from 'lib-trend-core';
import { Subject, combineLatest, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import * as ChatActions from '../../state/chat.actions';
import {
  conversationsSelector,
  presenceViewsSelector,
  userDisplaySelector,
} from '../../state/chat.selectors';
import { resolveChatInitials } from '../../chat-initials.util';
import { ChatPresenceView } from '../../chat-status/chat-status.util';

/** Directory lookups only start once the term is meaningful (same as T-24). */
const PEOPLE_SEARCH_MIN_LENGTH = 2;

/** Server-side cap for the directory slice merged into the picker. */
const PEOPLE_SEARCH_LIMIT = 10;

/** Dialog input — which conversation the invites are for. */
export interface ChatChannelInviteDialogData {
  channelId: string;
  /** Conversation name, shown in the subtitle. */
  channelName: string;
}

interface ChatInvitePersonOption {
  id: string;
  name: string;
  image?: string;
  initial: string;
  /** Already an active member — listed but not selectable. */
  alreadyMember: boolean;
}

/**
 * Internal Chat "Convidar pessoas" dialog (SPEC-2026-07-internal-chat, T-35 ·
 * RF-04).
 *
 * People picker over the bootstrap `userDisplay` map plus the company directory
 * (`GET /api/chat/search` → `data.people`, company-scoped server-side) — the
 * same pattern as the "Nova conversa" dialog. Multi-select; confirming sends
 * CHANNEL_INVITE `{ channelId, memberIds }`.
 *
 * Server-side rules mirrored here (channel.service.ts processInvite): invites
 * exist for PUBLIC/PRIVATE channels and GROUPs (never DIRECT — DEV-1984 opened
 * PUBLIC), the inviter must be an active member, and current members are never
 * re-invited. The refreshed roster arrives through the `invited` fan-out plus
 * the bootstrap refresh effect.
 */
@Component({
  templateUrl: './chat-channel-invite-dialog.component.html',
  styleUrls: ['./chat-channel-invite-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatChannelInviteDialogComponent extends AbstractComponent implements OnInit {

  people: ChatInvitePersonOption[] = [];
  filteredPeople: ChatInvitePersonOption[] = [];
  filterTerm = '';
  selectedIds = new Set<string>();
  submitting = false;
  loadingDirectory = false;

  /** Active members of the conversation — offered as "já participa". */
  private memberIds = new Set<string>();
  /** People resolved through the directory search, merged into `people`. */
  private directoryPeople: ChatUserDisplayMap = {};
  /** userId → presence view (dot class) for the avatar tiles. */
  private presenceViews: Record<string, ChatPresenceView> = {};
  private readonly terms = new Subject<string>();

  constructor(
    injector: Injector,
    private readonly dialogRef: MatDialogRef<ChatChannelInviteDialogComponent>,
    private readonly store: Store,
    private readonly chatSocketService: ChatSocketService,
    private readonly chatService: ChatService,
    @Inject(MAT_DIALOG_DATA) public readonly data: ChatChannelInviteDialogData,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    combineLatest([
      this.store.select(conversationsSelector),
      this.store.select(userDisplaySelector),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([conversations, userDisplay]) => {
        const conversation = conversations.find((item) => item._id === this.data?.channelId);
        this.memberIds = new Set(conversation?.members ?? []);
        this.rebuildPeople(userDisplay);
      });

    // Presence + custom status feed the dot on each avatar tile, exactly like
    // the conversation pane.
    this.store
      .select(presenceViewsSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((views) => this.presenceViews = views);

    this.terms
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((term) => {
          if (term.trim().length < PEOPLE_SEARCH_MIN_LENGTH) {
            this.loadingDirectory = false;
            return of<ChatSearchPerson[]>([]);
          }
          this.loadingDirectory = true;
          return this.chatService.search({ q: term.trim(), limit: PEOPLE_SEARCH_LIMIT })
            .pipe(
              map((envelope) => envelope?.data?.people ?? []),
              catchError(() => of<ChatSearchPerson[]>([])),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((people) => {
        this.loadingDirectory = false;
        if (people.length === 0) return;

        const resolved: ChatUserDisplayMap = {};
        for (const person of people) {
          resolved[person._id] = { name: person.name, image: person.image };
        }
        this.directoryPeople = { ...this.directoryPeople, ...resolved };
        // Share the resolution with every other chat surface (merge only).
        this.store.dispatch(ChatActions.mergeUserDisplay({ userDisplay: resolved }));
      });
  }

  applyFilter(): void {
    this.terms.next(this.filterTerm);
    this.applyLocalFilter();
  }

  toggle(person: ChatInvitePersonOption): void {
    if (person.alreadyMember) return;
    if (this.selectedIds.has(person.id)) {
      this.selectedIds.delete(person.id);
    } else {
      this.selectedIds.add(person.id);
    }
  }

  isSelected(person: ChatInvitePersonOption): boolean {
    return this.selectedIds.has(person.id);
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  close(): void {
    this.dialogRef.close();
  }

  /** CHANNEL_INVITE — PUBLIC/PRIVATE channels and GROUPs (server-enforced). */
  confirm(): void {
    if (this.submitting || this.selectedCount === 0 || !this.data?.channelId) return;

    this.submitting = true;
    this.chatSocketService.sendCommand(ChatCommandTypeEnum.CHANNEL_INVITE, {
      channelId: this.data.channelId,
      memberIds: Array.from(this.selectedIds),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Roster/membership truth comes from the `invited` fan-out; the
          // bootstrap refresh keeps the sidebar and the pane consistent.
          this.store.dispatch(ChatActions.loadBootstrap());
          this.alertService.success(this.t('chat.invite.success'));
          this.dialogRef.close(true);
        },
        error: (ack) => {
          this.submitting = false;
          this.alertService.error(this.resolveAckError(ack?.errorKey));
        },
      });
  }

  trackPerson(_index: number, person: ChatInvitePersonOption): string {
    return person.id;
  }

  /** Dot class for a person's avatar tile — same semantics as everywhere else. */
  dotFor(person: ChatInvitePersonOption): string {
    return this.presenceViews[person.id]?.dotClass ?? 'bg-secondary';
  }

  /** Bootstrap map + directory results, deduplicated by id, self excluded. */
  private rebuildPeople(userDisplay: ChatUserDisplayMap): void {
    const currentUserId = this.getIDCurrentUser();
    const merged: ChatUserDisplayMap = { ...userDisplay, ...this.directoryPeople };

    this.people = Object.keys(merged)
      .filter((id) => id !== currentUserId)
      // Removed/blocked/off-company users stay out of the picker — the
      // bootstrap flags them `inactive` (see ChatUserDisplay).
      .filter((id) => !merged[id]?.inactive)
      .map((id) => ({
        id,
        name: merged[id]?.name ?? '',
        image: merged[id]?.image,
        initial: resolveChatInitials(merged[id]?.name || '?'),
        alreadyMember: this.memberIds.has(id),
      }))
      .filter((person) => !!person.name)
      .sort((a, b) => (a.alreadyMember === b.alreadyMember ? a.name.localeCompare(b.name) : (a.alreadyMember ? 1 : -1)));

    this.applyLocalFilter();
  }

  private applyLocalFilter(): void {
    const term = this.filterTerm.trim().toLowerCase();
    this.filteredPeople = term
      ? this.people.filter((person) => person.name.toLowerCase().includes(term) || this.selectedIds.has(person.id))
      : this.people;
  }

  /** Translates a backend/synthetic ack errorKey, falling back when unknown. */
  private resolveAckError(errorKey?: string): string {
    if (errorKey) {
      const translated = this.t(errorKey);
      if (translated !== errorKey) return translated;
    }
    return this.t('chat.errors.commandFailed');
  }
}
